import { callApi } from '@/api/callApi';
import { API_ENDPOINT, API_URL_ORDER_SERVICE } from '@/api/constant';
import CommonFilter from '@/components/common/CommonFilter';
import CommonTable, { TableColumn } from '@/components/common/CommonTable';
import ExportOption from '@/components/common/export/ExportOption';
import PageHeading from '@/components/common/PageHeading';
import Pagination from '@/components/common/Pagination';
import Layout from '@/components/Layout/Layout';
import AssignDriver from '@/components/order/AssignDriver';
import OrderStatusUpdate from '@/components/order/OrderStatus';
import Card from '@/components/UI/Card';
import DateRangePicker from '@/components/UI/DateRange';
import { useDebounce } from '@/hooks/useDebounce';
import { usePermissions } from '@/hooks/usePermissions';
import { useSelectableTable } from '@/hooks/useSelectableTable';
import { ActionConfig } from '@/interfaces/common.interface';
import {
  IOrderResponse,
  IOrderStates,
  Order,
} from '@/interfaces/order.interface';
import {
  formatDateTime,
  GetFilterUrl,
  getPaymentMethodMessage,
  orderFilterOption,
  toFixedThree,
} from '@/utility/helper';
import { RoutesLink } from '@/utility/RouteLinks';
import { useEffect, useMemo, useState } from 'react';
import { Range } from 'react-date-range';
import { useNavigate } from 'react-router-dom';

const Orders: React.FC = () => {
  const { canView, canEdit } = usePermissions();
  const [searchValue, setSearchValue] = useState('');
  const debounceValue = useDebounce(searchValue);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);
  const navigate = useNavigate();
  const [state, setState] = useState<IOrderStates>({
    orderStatus: '',
    type: '',
    isJunk: null,
    order: [],
    pageInfo: {
      total: 10,
      totalPages: 1,
      limit: 10,
      page: 1,
    },
    isLoading: false,
  });

  const [dateRange, setDateRange] = useState<Range[]>([
    {
      startDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1),
      endDate: new Date(),
      key: 'selection',
    },
  ]);

  const handleChangeState = (newState: Partial<typeof state>) => {
    setState((prev) => ({
      ...prev,
      ...newState,
    }));
  };

  const { selectedItems, handleSelectAll, handleSelectItem } =
    useSelectableTable(state.order);

  useEffect(() => {
    fetchOrders();
  }, [
    state.pageInfo.page,
    state.pageInfo.limit,
    state.orderStatus,
    state.type,
    state.isJunk,
    debounceValue,
    dateRange,
  ]);

  const fetchOrders = async () => {
    try {
      const startDate = formatDateTime(dateRange[0].startDate, 'YYMMDD');
      const endDate = formatDateTime(dateRange[0].endDate, 'YYMMDD');
      const filterQuery = {
        page: state.pageInfo.page,
        limit: state.pageInfo.limit,
        status: state.orderStatus,
        type: state.type,
        search: debounceValue,
        startDate: startDate,
        endDate: endDate,
        isJunk: state.isJunk,
      };
      const query = GetFilterUrl(filterQuery);
      handleChangeState({ isLoading: true });
      const PATH = `${API_URL_ORDER_SERVICE}${API_ENDPOINT.order}?${query ? `${query}` : ''}`;
      const res = await callApi<IOrderResponse>(PATH, { method: 'GET' });
      handleChangeState({
        order: res.data,
        pageInfo: {
          limit: +res.pageInfo.limit,
          page: +res.pageInfo.page,
          total: +res.pageInfo.total,
          totalPages: +res.pageInfo.totalPages,
        },
        isLoading: false,
      });
    } catch (error) {
      handleChangeState({ isLoading: false });
    }
  };

  const handleChangePage = (page: number) => {
    handleChangeState({
      pageInfo: {
        ...state.pageInfo,
        page,
      },
    });
  };

  const handleChangePageSize = (limit: number) => {
    handleChangeState({
      pageInfo: {
        ...state.pageInfo,
        page: 1,
        limit,
      },
    });
  };

  const orderColumns: TableColumn<Order>[] = [
    {
      key: 'orderId',
      header: 'Order ID',
      render: (order) => (
        <span className={order.personalInfo.isEmployee ? 'text-blue-500' : ''}>
          {order.orderId}
        </span>
      ),
      className: 'p-3',
    },
    {
      key: 'dateTime',
      header: 'Date/Time',
      render: (order) => (
        <span className={order.personalInfo.isEmployee ? 'text-blue-500' : ''}>
          {formatDateTime(order.createdAt, 'YYMMMDDHH')}
        </span>
      ),
    },
    {
      key: 'customer',
      header: 'Customer',
      render: (order) => (
        <span className={order.personalInfo.isEmployee ? 'text-blue-500' : ''}>
          {order.personalInfo.firstName} {order.personalInfo.lastName}
        </span>
      ),
    },
    {
      key: 'total',
      header: 'Total Amount BD',
      render: (order) => (
        <span className={order.personalInfo.isEmployee ? 'text-blue-500' : ''}>
          {toFixedThree(order.grandTotal)}
        </span>
      ),
    },
    {
      key: 'orderStatus',
      header: 'Order Status',
      render: (order) => (
        <OrderStatusUpdate order={order} onSucess={() => fetchOrders()} />
      ),
    },
    {
      key: 'paymentType',
      header: 'Payment Type',
      render: (order) => (
        <span className={order.personalInfo.isEmployee ? 'text-blue-500' : ''}>
          {getPaymentMethodMessage(order.paymentMethod?.type) ?? 'N/A'}
        </span>
      ),
    },
    // {
    //   key: 'deliveryStatus',
    //   header: 'Delivery Status',
    //   render: (order) => order.deliveryStatus,
    // },
    {
      key: 'items',
      header: 'Items',
      render: (order) => (
        <span className={order.personalInfo.isEmployee ? 'text-blue-500' : ''}>
          {order.orderItems?.length ?? 0}
        </span>
      ),
    },
  ];

  const orderActionConfig: Record<string, ActionConfig<Order>> = {
    view: {
      text: 'View',
      onClick: (order) => navigate(`${RoutesLink.orderDetail}/${order.id}`),
      permission: () => canView('order'),
    },
  };

  const actions = useMemo(
    () =>
      Object.values(orderActionConfig)
        .filter((action) => action.permission())
        .map(({ permission, ...rest }) => rest),
    [canView, canEdit],
  );

  const filterOptions = [
    { value: 'customers', label: 'Customers' },
    { value: 'employee', label: 'Employees' },
  ];

  return (
    <Layout>
      <PageHeading text="Orders" />
      <Card>
        <div className="flex w-full mb-4 items-center justify-between gap-3 flex-wrap">
          <CommonFilter
            filters={orderFilterOption}
            onChange={(filter) =>
              filter === 'isJunk'
                ? handleChangeState({
                    orderStatus: '',
                    isJunk: true,
                    pageInfo: { ...state.pageInfo, page: 1 },
                  })
                : handleChangeState({
                    isJunk: null,
                    orderStatus: filter,
                    pageInfo: { ...state.pageInfo, page: 1 },
                  })
            }
            inputPlaceHolder="Search for Orders..."
            onSearch={(text) => setSearchValue(text)}
            filterOptions={{
              values: filterOptions,
              onChange: (value) => handleChangeState({ type: value }),
            }}
          />
          <div className="flex items-center gap-3 flex-wrap">
            <DateRangePicker
              dateRange={dateRange}
              setDateRange={setDateRange}
            />
            {state.order.length !== 0 && (
              <ExportOption
                APIPath={`${API_URL_ORDER_SERVICE}${API_ENDPOINT.order}/download-report`}
                dateRange={dateRange[0]}
                status={state.orderStatus}
              />
            )}
          </div>
        </div>
        <CommonTable
          columns={orderColumns}
          data={state.order}
          showCheckbox={false}
          selectedItems={selectedItems}
          onSelectAll={handleSelectAll}
          onSelectItem={handleSelectItem}
          getItemId={(item) => item.id}
          emptyMessage="No Order found"
          loading={state.isLoading}
          actions={actions}
        />
        <Pagination
          isLoading={state.isLoading}
          paginationData={state.pageInfo}
          onPageChange={(page) => handleChangePage(page)}
          onPageSizeChange={handleChangePageSize}
        />
        {isModalOpen && selectedOrderId && (
          <AssignDriver
            id={selectedOrderId}
            setIsOpen={setIsModalOpen}
            onSuccess={() => {
              (fetchOrders(), setSelectedOrderId(null));
            }}
          />
        )}
      </Card>
    </Layout>
  );
};

export default Orders;
